import type {
  ContentObject,
  ContentPreviewConfigEntry,
  ContentPreviewObject,
} from '../../lib/flotiq-client/types/api-response';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Panel from '../../components/Panel/Panel';

import { useContentObject } from '../../hooks/api';

import PreviewLinks from './PreviewLinks/PreviewLinks';

const PREVIEW_CTD = '_content_preview';
const CO_ID = 'flotiq.content-publishing';

interface ContentPreviewPanelProps {
  /**
   * Name of the content type definition of object the form is editing
   */
  contentTypeName?: string;
  /**
   * The content object currently being edited
   */
  contentObject?: ContentObject | null;
  /**
   * Whether a new object is being created
   */
  create?: boolean;
  /**
   * Whether the object is being duplicated
   */
  duplicate?: boolean;
  /**
   * Whether the panel is hidden
   */
  hidden?: boolean;
  /**
   * Whether the content type uses the draft/public publishing workflow
   */
  draftPublic?: boolean;
  /**
   * Whether the form has unsaved changes
   */
  isDirty?: boolean;
  /**
   * Saves the form from the panel; resolves with the saved object or null
   */
  onPanelSave?: () => Promise<ContentObject | null>;
  /**
   * Test id for the panel
   */
  testId?: string;
}

const ContentPreviewPanel = ({
  contentTypeName,
  contentObject,
  create,
  duplicate,
  hidden,
  draftPublic,
  isDirty,
  onPanelSave,
  testId,
}: ContentPreviewPanelProps) => {
  const { t } = useTranslation();

  const { entity: settingsObject } = useContentObject(PREVIEW_CTD, CO_ID);
  const settings = settingsObject as ContentPreviewObject | undefined;

  const isContentPreviewConfigEntry = (
    entry: ContentPreviewConfigEntry,
    contentTypeName: string,
  ) => {
    if (!entry.route_template) return false;
    return (
      !entry.content_types?.length ||
      entry.content_types.some((ctd) => ctd === contentTypeName)
    );
  };

  const entries = useMemo<ContentPreviewConfigEntry[]>(() => {
    if (!settings?.config?.length || !contentTypeName) return [];
    return settings.config.filter((entry) =>
      isContentPreviewConfigEntry(entry, contentTypeName),
    );
  }, [settings, contentTypeName]);

  if (duplicate) return null;
  if (!contentObject && !create) return null;
  if (!entries.length) return null;

  return (
    <Panel
      title={t('ContentPreview.Preview')}
      open
      isCollapsable={false}
      additionalContainerClasses={hidden ? 'xl:hidden px-4' : 'px-4'}
      additionalTitleClasses="text-lg"
      additionalChildrenContainerClasses="flex flex-col gap-2"
      testId={testId}
      id="content_object-content-preview"
    >
      {entries.map((config, idx) => (
        <PreviewLinks
          key={`${config.route_template}-${idx}`}
          config={config}
          baseUrl={settings?.base_url}
          editorKey={settings?.editor_key}
          contentObject={contentObject}
          create={create}
          isDirty={isDirty}
          draftPublic={draftPublic}
          onPanelSave={onPanelSave}
          testId={testId}
        />
      ))}
    </Panel>
  );
};

export default ContentPreviewPanel;
